import type { Block, Inline, ListItem, Row } from "./types.js";

export type StreamBufferCallbacks = {
  onUpdate(blocks: Block[]): void;
  onComplete(blocks: Block[]): void;
  // Fired once the answer is "settled": the visible prose is fully revealed (including
  // the trailing list item / table row normally held back) but the message has not
  // formally completed yet because the trailing follow-ups are still generating. Unlike
  // onComplete it keeps the message pending; the typewriter still finishes via complete().
  onSettled?(blocks: Block[]): void;
};

export type StreamBuffer = {
  setTargetBlocks(blocks: Block[]): void;
  complete(blocks?: Block[]): void;
  // Reveals everything immediately (including the held-back streaming edge) and fires
  // onSettled without completing. A later complete() still delivers the final answer.
  requestSettle(): void;
  stop(): void;
};

export type StreamBufferFactory = (
  callbacks: StreamBufferCallbacks,
) => StreamBuffer;

const tickDelay = 24;

export const createBlockTypewriter: StreamBufferFactory = (callbacks) => {
  let targetBlocks: Block[] = [];
  let revealedChars = 0; // characters revealed across the whole message
  let lastVisible: Block[] = []; // monotonic high-water of what has been shown
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let completionRequested = false;
  let completionEmitted = false;
  let settleRequested = false;
  let hasRevealedFirst = false;

  function totalChars(): number {
    return messageCharTotal(targetBlocks);
  }

  function emitUpdate(): void {
    if (stopped) return;
    // While the answer is still streaming the backend keeps appending tokens to
    // the final line, so revealing that growing edge would show a half-formed
    // bullet or table row that then fills in pieces. Hold the still-forming
    // trailing list item / table row back until a later snapshot proves it is
    // settled or the stream completes; paragraphs keep their typewriter reveal.
    const source =
      completionRequested || settleRequested
        ? targetBlocks
        : dropStreamingEdge(targetBlocks);
    // The backend re-parses the whole answer on every chunk, so a markdown reflow
    // can make an already-revealed unit cost more than the current budget. Merging
    // the fresh budget slice with what was shown before keeps the reveal monotonic,
    // so a revealed list item or table row never disappears and re-appears.
    const sliced = sliceBlocksToBudget(source, revealedChars);
    const merged = mergeMonotonicBlocks(lastVisible, sliced);
    // Guard the rare re-parse that fuses top-level blocks: never keep more blocks
    // than the current target actually has, so a stale block cannot linger.
    lastVisible =
      merged.length > targetBlocks.length
        ? merged.slice(0, targetBlocks.length)
        : merged;
    callbacks.onUpdate(lastVisible);
  }

  function emitComplete(): void {
    if (!completionRequested || completionEmitted) return;
    if (revealedChars < totalChars()) return;
    completionEmitted = true;
    callbacks.onComplete(targetBlocks);
  }

  function schedule(): void {
    if (stopped || timer) return;
    if (revealedChars >= totalChars()) {
      emitComplete();
      return;
    }
    timer = setTimeout(tick, tickDelay);
  }

  function tick(): void {
    timer = null;
    if (stopped) return;
    const total = totalChars();
    const backlog = total - revealedChars;
    if (backlog <= 0) {
      emitComplete();
      return;
    }
    // The step grows with the entire buffered backlog, not just the block that
    // is currently animating, so deeply buffered answers catch up quickly while
    // the tail still types out gently.
    revealedChars = Math.min(total, revealedChars + getStepForBacklog(backlog));
    emitUpdate();
    schedule();
  }

  function revealEverythingInstantly(): void {
    revealedChars = totalChars();
  }

  function stopTimer(): void {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
  }

  return {
    setTargetBlocks(blocks: Block[]): void {
      targetBlocks = blocks ?? [];
      if (!hasRevealedFirst) {
        // First visible snapshot paints instantly so time-to-first-text stays
        // minimal; later snapshots animate the newly buffered characters.
        revealEverythingInstantly();
        hasRevealedFirst = true;
        emitUpdate();
        return;
      }
      // Cumulative snapshots only grow; clamp in case a snapshot ever shrinks.
      revealedChars = Math.min(revealedChars, totalChars());
      schedule();
    },
    complete(blocks?: Block[]): void {
      if (blocks) targetBlocks = blocks;
      completionRequested = true;
      completionEmitted = false;
      if (!hasRevealedFirst) {
        revealEverythingInstantly();
        hasRevealedFirst = true;
        emitUpdate();
        emitComplete();
        return;
      }
      revealedChars = Math.min(revealedChars, totalChars());
      schedule();
    },
    requestSettle(): void {
      // The follow-ups signal can only arrive after the answer streamed, and an actual
      // completion supersedes it. Ignore it once completing, already settled, or stopped.
      if (stopped || completionRequested || settleRequested) return;
      settleRequested = true;
      stopTimer();
      // Reveal the whole settled answer at once, including the trailing edge that was
      // held back, then surface it. The message stays pending until complete() lands.
      revealEverythingInstantly();
      hasRevealedFirst = true;
      emitUpdate();
      callbacks.onSettled?.(targetBlocks);
    },
    stop(): void {
      stopped = true;
      stopTimer();
    },
  };
};

export function getStepForBacklog(backlog: number): number {
  if (backlog < 120) return 3;
  if (backlog < 600) return 10;
  if (backlog < 2000) return 28;
  if (backlog < 6000) return 80;
  return 160;
}

export function messageCharTotal(blocks: Block[]): number {
  let total = 0;
  for (const block of blocks) total += blockCharCost(block);
  return total;
}

function blockCharCost(block: Block): number {
  switch (block.type) {
    case "paragraph":
    case "heading":
      return countInlineChars(block.inlines);
    case "bulletList":
    case "orderedList":
      return block.items.reduce(
        (sum, item) => sum + messageCharTotal(item.blocks),
        0,
      );
    case "blockquote":
      return messageCharTotal(block.blocks);
    case "codeBlock":
      return block.text.length;
    case "table":
      return (
        (block.head ? rowCharCost(block.head) : 0) +
        block.rows.reduce((sum, row) => sum + rowCharCost(row), 0)
      );
    default:
      return 1; // thematicBreak is atomic
  }
}

function rowCharCost(row: Row): number {
  return row.cells.reduce(
    (sum, cell) => sum + countInlineChars(cell.inlines),
    0,
  );
}

// While an answer is still streaming the backend re-parses the whole markdown on
// every chunk, so the final line keeps growing token by token. Revealing that
// growing edge would show a half-formed bullet or table row that then fills in
// pieces, instead of appearing whole per line. We therefore hold back the trailing
// list item / table row until a later snapshot proves it is settled (a following
// line appeared) or the stream completes. Paragraphs, headings and code keep
// streaming character by character, which is the intended typewriter effect.
export function dropStreamingEdge(blocks: Block[]): Block[] {
  if (blocks.length === 0) return blocks;
  const lastIndex = blocks.length - 1;
  const last = blocks[lastIndex];
  if (!last) return blocks;
  const trimmed = trimEdgeBlock(last);
  if (trimmed === last) return blocks; // the trailing block streams as-is
  const head = blocks.slice(0, lastIndex);
  return trimmed ? [...head, trimmed] : head;
}

// Returns the same block reference when nothing is held back (the caller keeps it),
// a trimmed block when the still-forming trailing line was removed but earlier
// content remains, or null when the block becomes empty and is dropped entirely.
function trimEdgeBlock(block: Block): Block | null {
  switch (block.type) {
    case "bulletList":
      return block.items.length <= 1
        ? null
        : { type: "bulletList", items: block.items.slice(0, -1) };
    case "orderedList":
      return block.items.length <= 1
        ? null
        : {
            type: "orderedList",
            start: block.start,
            items: block.items.slice(0, -1),
          };
    case "table": {
      if (block.rows.length === 0) return block; // head only: already complete
      const rows = block.rows.slice(0, -1);
      if (!block.head && rows.length === 0) return null;
      return { type: "table", head: block.head, rows, align: block.align };
    }
    case "blockquote": {
      const inner = dropStreamingEdge(block.blocks);
      if (inner === block.blocks) return block;
      return inner.length ? { type: "blockquote", blocks: inner } : null;
    }
    default:
      return block; // paragraph / heading / codeBlock / thematicBreak
  }
}

// Reveal blocks against a single global character budget so the typewriter speed
// reflects everything that is buffered, not only the block currently animating.
export function sliceBlocksToBudget(blocks: Block[], budget: number): Block[] {
  const result: Block[] = [];
  let used = 0;
  for (const block of blocks) {
    const remaining = budget - used;
    if (remaining <= 0) break;
    const cost = blockCharCost(block);
    if (cost <= remaining) {
      result.push(block);
      used += cost;
      continue;
    }
    const partial = sliceBlockByChars(block, remaining);
    if (partial) result.push(partial);
    break;
  }
  return result;
}

function sliceBlockByChars(block: Block, budget: number): Block | null {
  if (budget <= 0) return null;
  switch (block.type) {
    case "paragraph": {
      const { inlines } = sliceInlines(block.inlines, budget);
      return inlines.length ? { type: "paragraph", inlines } : null;
    }
    case "heading": {
      const { inlines } = sliceInlines(block.inlines, budget);
      return inlines.length
        ? { type: "heading", level: block.level, inlines }
        : null;
    }
    case "codeBlock": {
      const text = block.text.slice(0, budget);
      return text
        ? { type: "codeBlock", language: block.language, text }
        : null;
    }
    case "blockquote": {
      const blocks = sliceBlocksToBudget(block.blocks, budget);
      return blocks.length ? { type: "blockquote", blocks } : null;
    }
    case "bulletList": {
      const items = sliceItemsToBudget(block.items, budget);
      return items.length ? { type: "bulletList", items } : null;
    }
    case "orderedList": {
      const items = sliceItemsToBudget(block.items, budget);
      return items.length
        ? { type: "orderedList", start: block.start, items }
        : null;
    }
    case "table":
      return sliceTableByChars(block, budget);
    default:
      return null; // thematicBreak is atomic and never partially revealed
  }
}

function sliceItemsToBudget(items: ListItem[], budget: number): ListItem[] {
  // List items are atomic reveal units, mirroring table rows: an item appears
  // whole or not at all so a half-formed bullet never flashes on screen. The
  // global budget still paces *when* each item appears.
  const result: ListItem[] = [];
  let used = 0;
  for (const item of items) {
    const cost = messageCharTotal(item.blocks);
    if (used + cost > budget) break;
    result.push(item);
    used += cost;
  }
  return result;
}

function sliceTableByChars(
  block: Extract<Block, { type: "table" }>,
  budget: number,
): Block | null {
  // The header anchors the table, so reveal it as a unit, then add whole rows as
  // the budget grows. Cells are never split mid-row.
  let used = block.head ? rowCharCost(block.head) : 0;
  const rows: Row[] = [];
  for (const row of block.rows) {
    const cost = rowCharCost(row);
    if (used + cost > budget) break;
    rows.push(row);
    used += cost;
  }
  if (!block.head && rows.length === 0) return null;
  return { type: "table", head: block.head, rows, align: block.align };
}

// Both inputs are in-order prefixes of the same growing message (the budget slice of
// the current target, and the high-water of what was shown before). Merging them per
// position — keeping whichever reveals more — guarantees the visible reveal only ever
// grows, so a unit that was already revealed never vanishes when a re-parse reflow
// makes an earlier unit larger than the current budget.
export function mergeMonotonicBlocks(prev: Block[], next: Block[]): Block[] {
  const length = Math.max(prev.length, next.length);
  const result: Block[] = [];
  for (let i = 0; i < length; i++) {
    const previous = prev[i];
    const current = next[i];
    if (current === undefined) {
      if (previous !== undefined) result.push(previous);
      continue;
    }
    if (previous === undefined) {
      result.push(current);
      continue;
    }
    result.push(mergeBlock(previous, current));
  }
  return result;
}

function mergeBlock(prev: Block, next: Block): Block {
  if (prev.type !== next.type) {
    // A structural reclassification of already-revealed content is rare; prefer the
    // fresh parse so a stale shape is never rendered.
    return next;
  }
  if (
    prev.type === "paragraph" ||
    prev.type === "heading" ||
    prev.type === "codeBlock"
  ) {
    return blockCost(next) >= blockCost(prev) ? next : prev;
  }
  if (prev.type === "blockquote" && next.type === "blockquote") {
    return {
      type: "blockquote",
      blocks: mergeMonotonicBlocks(prev.blocks, next.blocks),
    };
  }
  if (prev.type === "bulletList" && next.type === "bulletList") {
    return { type: "bulletList", items: mergeItems(prev.items, next.items) };
  }
  if (prev.type === "orderedList" && next.type === "orderedList") {
    return {
      type: "orderedList",
      start: next.start,
      items: mergeItems(prev.items, next.items),
    };
  }
  if (prev.type === "table" && next.type === "table") {
    return {
      type: "table",
      head: next.head ?? prev.head,
      rows: mergeRows(prev.rows, next.rows),
      align: next.align.length ? next.align : prev.align,
    };
  }
  return next; // thematicBreak is atomic
}

function mergeItems(prev: ListItem[], next: ListItem[]): ListItem[] {
  const length = Math.max(prev.length, next.length);
  const result: ListItem[] = [];
  for (let i = 0; i < length; i++) {
    const previous = prev[i];
    const current = next[i];
    if (current === undefined) {
      if (previous !== undefined) result.push(previous);
      continue;
    }
    if (previous === undefined) {
      result.push(current);
      continue;
    }
    result.push({ blocks: mergeMonotonicBlocks(previous.blocks, current.blocks) });
  }
  return result;
}

function mergeRows(prev: Row[], next: Row[]): Row[] {
  const length = Math.max(prev.length, next.length);
  const result: Row[] = [];
  for (let i = 0; i < length; i++) {
    const previous = prev[i];
    const current = next[i];
    if (current === undefined) {
      if (previous !== undefined) result.push(previous);
      continue;
    }
    if (previous === undefined) {
      result.push(current);
      continue;
    }
    // Rows reveal whole or not at all, so the only divergence is a cell growing on a
    // re-parse; keep whichever carries more characters.
    result.push(rowCharCost(current) >= rowCharCost(previous) ? current : previous);
  }
  return result;
}

function blockCost(block: Block): number {
  return messageCharTotal([block]);
}

function countInlineChars(inlines: Inline[]): number {
  let total = 0;
  for (const inline of inlines) total += inlineCharCount(inline);
  return total;
}

function inlineCharCount(inline: Inline): number {
  switch (inline.type) {
    case "text":
    case "code":
      return inline.value.length;
    case "strong":
    case "emphasis":
    case "link":
      return countInlineChars(inline.inlines);
    case "lineBreak":
    case "pill":
      return 1;
    default:
      return 0;
  }
}

function sliceInlines(
  inlines: Inline[],
  budget: number,
): { inlines: Inline[]; used: number } {
  const result: Inline[] = [];
  let used = 0;
  for (const inline of inlines) {
    if (used >= budget) break;
    const remaining = budget - used;
    const cost = inlineCharCount(inline);
    if (cost <= remaining) {
      result.push(inline);
      used += cost;
      continue;
    }
    const partial = sliceInlinePartial(inline, remaining);
    if (partial.inline) result.push(partial.inline);
    used += partial.used;
    break;
  }
  return { inlines: result, used };
}

function sliceInlinePartial(
  inline: Inline,
  budget: number,
): { inline: Inline | null; used: number } {
  switch (inline.type) {
    case "text": {
      const value = inline.value.slice(0, budget);
      return {
        inline: value ? { type: "text", value } : null,
        used: value.length,
      };
    }
    case "code": {
      const value = inline.value.slice(0, budget);
      return {
        inline: value ? { type: "code", value } : null,
        used: value.length,
      };
    }
    case "strong":
    case "emphasis": {
      const sliced = sliceInlines(inline.inlines, budget);
      return {
        inline: sliced.inlines.length
          ? { type: inline.type, inlines: sliced.inlines }
          : null,
        used: sliced.used,
      };
    }
    case "link": {
      const sliced = sliceInlines(inline.inlines, budget);
      return {
        inline: sliced.inlines.length
          ? { type: "link", href: inline.href, inlines: sliced.inlines }
          : null,
        used: sliced.used,
      };
    }
    default:
      return { inline: null, used: 0 }; // pill/lineBreak are atomic, never partial
  }
}
